import { Box, Typography } from "@mui/material";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts/highstock";
import React, { useEffect } from "react";
import {
  activeUserGraph,
  jobApplicationOverview,
  topJobCategories,
  analyticsExportReport,
} from "./services";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { downloadBinaryFile } from "../../utils/utils";
import { toast } from "react-toastify";

const Analytics = () => {
  const [activeDataGraph, setActiveDataGraph] = React.useState([]);
  const [overViewGraph, setOverViewGraph] = React.useState([]);
  const [topCategoriesData, setTopCategoriesData] = React.useState([]);
  const [startDate, setStartDate] = React.useState(null);
  const [endDate, setEndDate] = React.useState(null);

  useEffect(() => {
    handleActiveDataGraph();
    handleJobOverviewGraph();
    handleTopJobCategories();
  }, []);

  useEffect(() => {
    handleJobOverviewGraph();
  }, [startDate, endDate]);

  const handleActiveDataGraph = async () => {
    try {
      const resp = await activeUserGraph();
      if (resp.status === 200 || resp.status === 201) {
        setActiveDataGraph(resp.data.data);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleExportReport = async () => {
    try{
      const params = {};
      if (startDate && endDate) {
        params.start_date = startDate.format("DD-MM-YYYY");
        params.end_date = endDate.format("DD-MM-YYYY");
      }
      const response = await analyticsExportReport(params);
      downloadBinaryFile(response, 'Analytics Report.xlsx');
    } catch (error) {
      // console.error("Error fetching API data:", error);
      toast.error("Error fetching report export data",{toastId:"exportReportError"});
    }
  };

  const handleJobOverviewGraph = async () => {
    try {
      const params = {};
      if (startDate && endDate) {
        params.start_date = startDate.format("DD-MM-YYYY");
        params.end_date = endDate.format("DD-MM-YYYY");
      }

      const resp = await jobApplicationOverview(params);
      if (resp.status === 200 || resp.status === 201) {
        setOverViewGraph(resp.data.payload);
      }
    } catch (err) {
      console.log(err);
    }
  };
  const handleTopJobCategories = async () => {
    try {
      const resp = await topJobCategories();
      if (resp.status === 200 || resp.status === 201) {
        setTopCategoriesData(resp.data.payload);
      }
    } catch (err) {
      console.log(err);
    }
  };
  const options = {
    chart: {
      type: "line",
      height: 300,
      zoomType: "x",
      panning: true,
      panKey: "shift",
    },
    title: {
      text: "",
    },
    xAxis: {
      categories: overViewGraph?.map((item) => item.month),
      scrollbar: {
        enabled: true,
      },
    },
    yAxis: {
      title: {
        text: "",
      },
    },
    legend: {
      layout: "horizontal",
      align: "center",
      verticalAlign: "bottom",
    },
    series: [
      {
        name: "Total Applications",
        data: overViewGraph?.map((item) => item.total_applications),
        color: "#7C8CB0",
      },
      {
        name: "Successful",
        data: overViewGraph?.map((item) => item.successful_applications),
        color: "#8BC7D6",
      },
    ],
    credits: {
      enabled: false,
    },
  };

  const optionsLine = {
    chart: {
      type: "line",
      height: 250,
      zoomType: "x",
      panning: true,
      panKey: "shift",
    },
    title: {
      text: "",
    },
    xAxis: {
      categories: activeDataGraph?.map((item) => item.hour),
      title: {
        text: null,
      },
      scrollbar: {
        enabled: true,
      },
      min: 0,
      max: 10,
    },
    yAxis: {
      title: {
        text: "",
      },
    },
    tooltip: {
      shared: true,
      formatter: function () {
        return `<b>${this.x}</b><br/>Active User: ${this.y}`;
      },
    },
    legend: {
      enabled: false,
    },
    series: [
      {
        name: "Active User",
        data: activeDataGraph?.map((e) => e.active_users),
        color: "#7C8CB0",
        marker: {
          enabled: true,
          radius: 4,
        },
      },
    ],
    credits: {
      enabled: false,
    },
  };

  const optionsBar = {
    chart: {
      type: "column",
      height: 300,
      zoomType: "x",
      panning: true,
      panKey: "shift",
    },
    title: {
      text: "",
    },
    xAxis: {
      categories: topCategoriesData?.map((item) => item.category) || [],
      crosshair: true,
      scrollbar: {
        enabled: true,
      },
      min: 0,
      max: 4,
    },
    yAxis: [
      {
        title: {
          text: "",
        },
      },
      {
        opposite: true,
        min: 0,
        max:
          Math.max(
            ...(topCategoriesData?.map((item) => item.total_applications) || [
              12,
            ])
          ) + 5,
        tickInterval: 2,
        title: {
          text: "",
        },
      },
    ],
    tooltip: {
      shared: true,
    },
    legend: {
      layout: "horizontal",
      align: "center",
      verticalAlign: "bottom",
    },
    plotOptions: {
      column: {
        pointPadding: 0.2,
        borderWidth: 0,
      },
    },
    series: [
      {
        name: "Total Jobs",
        data: topCategoriesData?.map((item) => item.total_jobs) || [],
        color: "#7C8CB0",
        yAxis: 0,
      },
      {
        name: "Applications",
        data: topCategoriesData?.map((item) => item.total_applications) || [],
        color: "#8BC7D6",
        yAxis: 1,
      },
    ],
    credits: {
      enabled: false,
    },
  };

  return (
    <>
      <Box
        sx={{
          backgroundColor: "#eaf3fc",
          minHeight: "94vh",
          padding: { xs: "14px", md: "36px" },
        }}
      >
        <Box
          sx={{
            background: "#ffffff",
            padding: "30px",
            border: "1px solid #1570EF",
            borderRadius: "8px",
          }}
        >
          {/* Header */}
          <Box
            display="flex"
            flexWrap="wrap"
            flexDirection={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            mb={3}
          >
            <Typography
              variant="h4"
              sx={{
                fontSize: { xs: "24px", md: "36px", sm: "32px" },
                fontWeight: 600,
              }}
            >
              Analytics
            </Typography>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DemoContainer components={["DatePicker"]}>
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={(newValue) => setStartDate(newValue)}
                    disableFuture
                  />
                </DemoContainer>
              </LocalizationProvider>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DemoContainer components={["DatePicker"]}>
                  <DatePicker
                    label="End Date"
                    value={endDate}
                    onChange={(newValue) => setEndDate(newValue)}
                    disableFuture
                  />
                </DemoContainer>
              </LocalizationProvider>
              <button
                className="api-integration-btn"
                style={{ marginTop: "8px" }}
                onClick={handleExportReport}
              >
                Export Report
              </button>
            </Box>
          </Box>

          <Typography variant="h6" mb={{ xs: 2, sm: 0 }} sx={{ mb: 2 }}>
            Job Application Overview
          </Typography>
          <Box>
            <HighchartsReact highcharts={Highcharts} options={options} />
          </Box>

          <Typography variant="h6" mb={{ xs: 2, sm: 0 }} sx={{ mb: 2, mt: 3 }}>
            User Activity by Hour
          </Typography>
          <Box>
            <HighchartsReact highcharts={Highcharts} options={optionsLine} />
          </Box>

          <Typography variant="h6" mb={{ xs: 2, sm: 0 }} sx={{ mb: 2, mt: 3 }}>
            Jobs by Category
          </Typography>
          <Box>
            <HighchartsReact highcharts={Highcharts} options={optionsBar} />
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default Analytics;
